import React, { useState, useEffect, useMemo, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { Calendar, List, Plus, Filter, Clock, MapPin, User, CheckCircle, AlertCircle, XCircle, RefreshCw, Eye, Edit, Trash2, Sparkles, Activity, TrendingUp, Zap } from 'lucide-react';
import { http } from '../../lib/api';
import AgendamientoForm from './AgendamientoForm';
import AgendamientoDetalle from './AgendamientoDetalle';
import { useAuth } from '../../context/AuthContext';
import './CalendarioStyles.css';
import { resolveDisplayName, buildDisplayNameFromMaybeObject, buildDisplayNameFromUser } from '../../lib/displayName';

export default function Agenda() {
  const { user } = useAuth();
  
  // Si no hay usuario autenticado, mostrar mensaje
  if (!user) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-600 text-lg font-medium mb-2">
            Acceso restringido
          </div>
          <div className="text-yellow-700">
            Debes iniciar sesión para acceder al sistema de agendamientos.
          </div>
        </div>
      </div>
    );
  }

  const [events, setEvents] = useState([]);
  const [agendamientos, setAgendamientos] = useState([]);
  const [vistaActual, setVistaActual] = useState('calendario'); // 'calendario' o 'lista'
  const [loading, setLoading] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [filtros, setFiltros] = useState({
    cliente: '',
    estado: '',
    tipo: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  
  // Estados para modales
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [agendamientoSeleccionado, setAgendamientoSeleccionado] = useState(null);
  const [modoFormulario, setModoFormulario] = useState('crear'); // 'crear', 'editar'
  
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [agendamientoParaDetalle, setAgendamientoParaDetalle] = useState(null);

  const estadoColors = {
    'pendiente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'confirmado': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'en_progreso': 'bg-purple-100 text-purple-800 border-purple-200',
    'completado': 'bg-green-100 text-green-800 border-green-200',
    'cancelado': 'bg-red-100 text-red-800 border-red-200',
    'reagendado': 'bg-orange-100 text-orange-800 border-orange-200'
  };

  const tipoColors = {
    'preventivo': '#10b981', // green
    'correctivo': '#f59e0b', // amber
    'revision': '#3b82f6', // blue
    'limpieza': '#8b5cf6', // purple
    'actualizacion': '#ef4444' // red
  };

  useEffect(() => {
    if (user) {
      cargarTecnicos();
      cargarClientes();
      cargarEquipos();
      // No cargamos agendamientos aquí para esperar a datesSet del calendario
    }
  }, [user]);

  const cargarTecnicos = async () => {
    try {
      const response = await http.get('/usuarios?rol=tecnico');
      setTecnicos(response.data || []);
    } catch (error) {
      console.error('Error cargando tecnicos:', error);
    }
  };

  const cargarClientes = async () => {
    try {
      const response = await http.get('/clientes');
      setClientes(response.data || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const cargarEquipos = async () => {
    try {
      const response = await http.get('/equipos/');
      setEquipos(response.data || []);
    } catch (error) {
      console.error('Error cargando equipos:', error);
    }
  };

  const cargarAgendamientos = useCallback(async (startStr = null, endStr = null) => {
    setLoading(true);
    try {
      let url = '/agendamientos/';
      // Solo filtrar por fechas si tenemos el rango (vista calendario)
      // Ojo: Si es vista de lista, tal vez queramos cargar todo o un rango default
      if (startStr && endStr) {
         // FullCalendar manda ISO strings
         // Ajustar formato si es necesario, pero el backend espera strings ISO simples o fechas YYYY-MM-DD
         // FullCalendar: 2023-10-01T00:00:00-03:00
         url += `?start_date=${startStr}&end_date=${endStr}`;
      }

      const response = await http.get(url);
      const data = Array.isArray(response.data) ? response.data : [];
      
      setAgendamientos(data);
      
      // Preparar eventos para el calendario
      const eventosCalendario = data.map(agendamiento => ({
        id: agendamiento.id || agendamiento._id,
        title: agendamiento.titulo,
        start: agendamiento.fecha_programada,
        end: agendamiento.hora_fin ? `${agendamiento.fecha_programada.split('T')[0]}T${agendamiento.hora_fin}:00` : agendamiento.fecha_programada,
        backgroundColor: tipoColors[agendamiento.tipo_mantenimiento] || '#6b7280',
        borderColor: tipoColors[agendamiento.tipo_mantenimiento] || '#6b7280',
        extendedProps: {
          originalData: agendamiento, // Guardamos todo el objeto para el modal
          cliente: agendamiento.cliente?.nombre || 'Sin cliente',
          estado: agendamiento.estado,
          tipo: agendamiento.tipo_mantenimiento
        }
      }));
      
      setEvents(eventosCalendario);
    } catch (error) {
      console.error('Error cargando agendamientos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDatesSet = (dateInfo) => {
    // API Call optimizada por rango de fechas
    cargarAgendamientos(dateInfo.startStr, dateInfo.endStr);
  };
  
  const handleEventClick = (clickInfo) => {
    const data = clickInfo.event.extendedProps.originalData;
    if (data) {
        setAgendamientoParaDetalle(data);
        setMostrarDetalle(true);
    }
  };

  const agendamientosFiltrados = agendamientos.filter(ag => {
    if (filtros.cliente && !ag.cliente?.nombre?.toLowerCase().includes(filtros.cliente.toLowerCase())) return false;
    if (filtros.estado && ag.estado !== filtros.estado) return false;
    if (filtros.tipo && ag.tipo_mantenimiento !== filtros.tipo) return false;
    return true;
  });


  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    try {
      return new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  const cambiarEstado = async (agendamientoId, nuevoEstado) => {
    try {
      await http.put(`/agendamientos/${agendamientoId}`, { estado: nuevoEstado });
      cargarAgendamientos();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      alert('Error al actualizar el estado');
    }
  };

  const eliminarAgendamiento = async (agendamientoId, titulo) => {
    const confirmacion = window.confirm(
      `¿Está seguro de eliminar el agendamiento "${titulo}"?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmacion) return;

    try {
      await http.delete(`/agendamientos/${agendamientoId}`);
      alert('Agendamiento eliminado exitosamente');
      cargarAgendamientos();
    } catch (error) {
      console.error('Error eliminando agendamiento:', error);
      alert('Error al eliminar el agendamiento: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Funciones para manejo del formulario
  const abrirFormularioCrear = () => {
    if (!puedeCrear()) return;
    setAgendamientoSeleccionado(null);
    setModoFormulario('crear');
    setMostrarFormulario(true);
  };

  const abrirFormularioEditar = (agendamiento) => {
    if (!puedeEditar()) return;
    setAgendamientoSeleccionado(agendamiento);
    setModoFormulario('editar');
    setMostrarFormulario(true);
  };

  const abrirFormularioVer = (agendamiento) => {
    setAgendamientoParaDetalle(agendamiento);
    setMostrarDetalle(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setAgendamientoSeleccionado(null);
    setModoFormulario('crear');
  };

  const handleGuardar = () => {
    cerrarFormulario();
    cargarAgendamientos();
  };

  const handleGuardadoFormulario = () => {
    cerrarFormulario();
    cargarAgendamientos();
  };

  const handleEliminar = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este agendamiento?')) {
      try {
        await http.delete(`/agendamientos/${id}`);
        cargarAgendamientos();
      } catch (error) {
        console.error('Error eliminando agendamiento:', error);
        alert('Error al eliminar el agendamiento');
      }
    }
  };

  // Funciones de permisos
  const puedeCrear = () => {
    return user?.rol === 'admin';
  };

  const puedeEditar = () => {
    return user?.rol === 'admin';
  };

  const puedeEliminar = () => {
    return user?.rol === 'admin';
  };

  const puedeVer = () => {
    return user?.rol === 'admin' || user?.rol === 'tecnico';
  };

  const esAdmin = () => {
    return user?.rol === 'admin';
  };

  // Funciones helper para colores e iconos
  const getColorEstado = (estado, isBadge = false) => {
    const colores = {
      'programado': isBadge ? 'bg-emerald-100 text-emerald-800' : 'bg-emerald-100 text-emerald-600',
      'en_progreso': isBadge ? 'bg-yellow-100 text-yellow-800' : 'bg-yellow-100 text-yellow-600',
      'completado': isBadge ? 'bg-green-100 text-green-800' : 'bg-green-100 text-green-600',
      'cancelado': isBadge ? 'bg-red-100 text-red-800' : 'bg-red-100 text-red-600',
      'pendiente': isBadge ? 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-600'
    };
    return colores[estado] || (isBadge ? 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-600');
  };

  const getIconoEstado = (estado) => {
    const iconos = {
      'programado': <Calendar className="w-5 h-5 text-emerald-600" />,
      'en_progreso': <Clock className="w-5 h-5 text-yellow-600" />,
      'completado': <CheckCircle className="w-5 h-5 text-green-600" />,
      'cancelado': <AlertCircle className="w-5 h-5 text-red-600" />,
      'pendiente': <AlertCircle className="w-5 h-5 text-gray-600" />
    };
    return iconos[estado] || <Calendar className="w-5 h-5 text-gray-600" />;
  };

  // Funciones para el calendario
  // handleEventClick ya está definido arriba

  const handleDateSelect = (selectInfo) => {
    if (puedeCrear()) {
      setModoFormulario('crear');
      setAgendamientoSeleccionado({
        fecha: selectInfo.startStr.split('T')[0],
        hora_inicio: '09:00',
        hora_fin: '10:00'
      });
      setMostrarFormulario(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-6">
      {/* Header Responsive - compacto en mobile */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 md:py-4 gap-3">
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 md:w-7 md:h-7 text-emerald-600 flex-shrink-0" />
                <span className="truncate">Agenda</span>
              </h1>
              <p className="text-slate-500 text-xs md:text-sm mt-0.5 hidden sm:block">
                Gestiona mantenimientos y visitas técnicas
              </p>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {puedeCrear() && (
                <button
                  onClick={abrirFormularioCrear}
                  className="hidden md:flex bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium items-center gap-2 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Nuevo Agendamiento
                </button>
              )}
              <button
                onClick={cargarAgendamientos}
                disabled={loading}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
                title="Actualizar"
              >
                <RefreshCw className={`w-4 h-4 md:w-5 md:h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              
              <div className="flex bg-slate-100 p-0.5 md:p-1 rounded-lg border border-slate-200">
                <button
                  onClick={() => setVistaActual('calendario')}
                  className={`px-2.5 py-1.5 md:px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                    vistaActual === 'calendario'
                      ? 'bg-white text-emerald-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Calendario</span>
                </button>
                <button
                  onClick={() => setVistaActual('lista')}
                  className={`px-2.5 py-1.5 md:px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                    vistaActual === 'lista'
                      ? 'bg-white text-emerald-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">Lista</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 space-y-4 md:space-y-6">
        {/* Stats Pills - Compact horizontal scroll on mobile, grid on desktop */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-4 md:gap-4 no-scrollbar snap-x">
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white rounded-xl border border-slate-200 shadow-sm snap-start flex-shrink-0 md:flex-shrink md:p-4">
            <div className="p-1.5 md:p-2.5 bg-emerald-50 rounded-lg">
              <Calendar className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-slate-900 leading-none">{agendamientos.length}</p>
              <p className="text-[10px] md:text-xs text-slate-500 font-medium">Total</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white rounded-xl border border-slate-200 shadow-sm snap-start flex-shrink-0 md:flex-shrink md:p-4">
            <div className="p-1.5 md:p-2.5 bg-emerald-50 rounded-lg">
              <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-emerald-600 leading-none">
                {agendamientos.filter(a => a.estado === 'completado').length}
              </p>
              <p className="text-[10px] md:text-xs text-slate-500 font-medium">Completados</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white rounded-xl border border-slate-200 shadow-sm snap-start flex-shrink-0 md:flex-shrink md:p-4">
            <div className="p-1.5 md:p-2.5 bg-amber-50 rounded-lg">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-amber-600 leading-none">
                {agendamientos.filter(a => a.estado === 'en_progreso').length}
              </p>
              <p className="text-[10px] md:text-xs text-slate-500 font-medium">En Proceso</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white rounded-xl border border-slate-200 shadow-sm snap-start flex-shrink-0 md:flex-shrink md:p-4">
            <div className="p-1.5 md:p-2.5 bg-slate-100 rounded-lg">
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-slate-600 leading-none">
                {agendamientos.filter(a => a.estado === 'pendiente').length}
              </p>
              <p className="text-[10px] md:text-xs text-slate-500 font-medium">Pendientes</p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px] md:min-h-[600px]">
          {vistaActual === 'calendario' ? (
            <div className="p-2 md:p-6 calendar-wrapper">
              <style>{`
                .fc-toolbar-title { font-size: 1.1rem !important; }
                .fc-button { font-size: 0.8rem !important; padding: 4px 8px !important; }
                .fc-button-group { gap: 0 !important; }
                @media (max-width: 768px) {
                  .fc-header-toolbar { 
                    flex-wrap: wrap; 
                    gap: 0.5rem !important; 
                    padding: 0.25rem !important;
                  }
                  .fc-toolbar-chunk { 
                    display: flex; 
                    justify-content: center;
                  }
                  .fc-toolbar-chunk:first-child { order: 1; }
                  .fc-toolbar-chunk:nth-child(2) { order: 0; width: 100%; }
                  .fc-toolbar-chunk:last-child { order: 2; }
                  .fc-toolbar-title { font-size: 1rem !important; font-weight: 700 !important; }
                  .fc-button { font-size: 0.7rem !important; padding: 3px 6px !important; }
                  .fc-daygrid-day-number { font-size: 0.8rem !important; padding: 2px 4px !important; }
                  .fc-daygrid-event { font-size: 0.65rem !important; }
                  .fc-col-header-cell-cushion { font-size: 0.7rem !important; padding: 4px 2px !important; }
                  .fc-timegrid-slot-label { font-size: 0.65rem !important; }
                  .fc-event-title { font-size: 0.65rem !important; }
                }
              `}</style>
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={window.innerWidth < 768 ? "timeGridDay" : "dayGridMonth"}
                locale={esLocale}
                headerToolbar={window.innerWidth < 768 ? {
                  left: 'prev,next',
                  center: 'title',
                  right: 'dayGridMonth,timeGridDay'
                } : {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={events}
                selectable={true}
                selectMirror={true}
                editable={false}
                eventClick={handleEventClick}
                datesSet={handleDatesSet}
                select={handleDateSelect}
                height="auto"
                contentHeight="auto"
                aspectRatio={window.innerWidth < 768 ? 1.0 : 1.5}
                eventDisplay="block"
                dayMaxEvents={window.innerWidth < 768 ? 2 : true}
                eventClassNames="cursor-pointer hover:opacity-90 transition-opacity"
              />
            </div>
          ) : (
            <div className="p-3 md:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                {agendamientosFiltrados.map((agendamiento) => {
                  const fechaObj = agendamiento.fecha_programada ? new Date(agendamiento.fecha_programada) : null;
                  const dia = fechaObj ? fechaObj.getDate() : '?';
                  const mes = fechaObj ? fechaObj.toLocaleDateString('es-ES', { month: 'short' }).toUpperCase() : '';
                  const esFuturo = fechaObj ? fechaObj >= new Date(new Date().setHours(0,0,0,0)) : false;
                  return (
                    <div
                      key={agendamiento._id}
                      onClick={() => abrirFormularioVer(agendamiento)}
                      className={`bg-white border rounded-xl p-3 md:p-4 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden active:scale-[0.99] ${
                        esFuturo ? 'border-slate-200 hover:border-emerald-200' : 'border-slate-100 opacity-75'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Date badge */}
                        <div className={`flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-xl flex flex-col items-center justify-center ${
                          esFuturo ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <span className="text-base md:text-lg font-bold leading-none">{dia}</span>
                          <span className="text-[9px] md:text-[10px] font-semibold leading-none mt-0.5">{mes}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 text-sm md:text-base truncate">
                              {agendamiento.cliente?.nombre || 'Cliente sin nombre'}
                            </h3>
                            <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium border ${getColorEstado(agendamiento.estado, true)}`}>
                              {agendamiento.estado}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-3 text-xs text-slate-500 mb-1.5">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {agendamiento.hora_inicio ? agendamiento.hora_inicio.substring(0,5) : '--:--'} - {agendamiento.hora_fin ? agendamiento.hora_fin.substring(0,5) : '--:--'}
                            </span>
                            {agendamiento.sucursal && (
                              <span className="flex items-center gap-1 truncate">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{agendamiento.sucursal}</span>
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {agendamiento.tecnico_asignado?.nombre || 'Sin técnico'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action buttons - visible on hover / always on mobile */}
                      {(puedeEditar() || puedeEliminar()) && (
                        <div className="flex justify-end gap-1 mt-2 pt-2 border-t border-slate-100">
                          {puedeEditar() && (
                            <button
                              onClick={(e) => { e.stopPropagation(); abrirFormularioEditar(agendamiento); }}
                              className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {puedeEliminar() && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEliminar(agendamiento._id); }}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {agendamientosFiltrados.length === 0 && (
                  <div className="col-span-full py-10 md:py-12 text-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-7 h-7 md:w-8 md:h-8 text-slate-400" />
                    </div>
                    <h3 className="text-base md:text-lg font-medium text-slate-900">No hay agendamientos</h3>
                    <p className="text-sm text-slate-500">No se encontraron eventos con los filtros actuales.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile FAB */}
      {puedeCrear() && (
        <button
          onClick={abrirFormularioCrear}
          className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-600/30 flex items-center justify-center hover:bg-emerald-700 active:scale-95 transition-all z-40"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Modal del formulario */}
      {mostrarFormulario && (
        <AgendamientoForm
          agendamiento={agendamientoSeleccionado}
          onGuardar={handleGuardar}
          onCancelar={cerrarFormulario}
          tecnicos={tecnicos}
          clientes={clientes}
          equipos={equipos}
          mode={modoFormulario}
          isReadOnly={modoFormulario === 'ver'}
        />
      )}

      {/* Modal Detalle */}
      {mostrarDetalle && agendamientoParaDetalle && (
         <AgendamientoDetalle
          agendamiento={agendamientoParaDetalle}
          onClose={() => setMostrarDetalle(false)}
          onEdit={(ag) => {
             setMostrarDetalle(false);
             abrirFormularioEditar(ag);
          }}
          onDelete={(ag) => {
            setMostrarDetalle(false);
            eliminarAgendamiento(ag._id || ag.id, ag.titulo);
          }}
          onCrearOrden={(ag) => {
            setMostrarDetalle(false);
            window.dispatchEvent(new CustomEvent('navigate', { 
              detail: { seccion: 'ordenes', agendamientoParaOrden: ag }
            }));
          }}
         />
      )}
    </div>
  );
}